import { useCallback } from 'react';
// components
import {
  CasRenderRelativeDateWithName,
  CasCRUDTable,
  CasRenderChanges,
} from 'src/compass/shared/components';
// constants
import { usePeriodDates } from 'src/compass/shared/hooks/usePeriodDates';
// utils
import prependAllOption from 'src/compass/shared/util/prependAllOption';
import { getColorByDateStatus } from 'src/compass/shared/util/getColorByDateStatus';
import { formatDate, DateFormat, getDateInMoment } from 'src/compass/shared/util/casMoment';
import ICasEditInfoProps from 'src/compass/shared/components/CasTable/ICasEditInfoProps';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';

export interface IChangesTabProps {
  onlyCurrentMonth?: boolean;
}

interface ICasChangesTable extends IChangesTabProps {
  entity: string;
  typeOptions: { name: string; id: string }[];
  getEditRowInfo: (id: number, type: string) => ICasEditInfoProps;
}

export default function CasChangesTable({
  entity,
  typeOptions,
  getEditRowInfo,
  onlyCurrentMonth = false,
}: ICasChangesTable) {
  const allTypeOptions = prependAllOption(typeOptions);

  const { data, isLoading } = usePeriodDates();
  const { currentPeriodStart } = data;

  const getPrevAndNextMonths = useCallback(
    (startDate: string) => {
      const formattedStartDate = getDateInMoment(startDate);
      const months = (onlyCurrentMonth ? [0] : [1, 0, -1, -2]).map((offset) => {
        const month = formattedStartDate.clone().add(offset, 'months');
        let name;

        const monthLabel = formatDate(month, DateFormat.MMMY);
        if (offset === 1) name = `Next month (${monthLabel})`;
        else if (offset === 0) name = `Current (${monthLabel})`;
        else name = monthLabel;

        return {
          name,
          id: formatDate(month, DateFormat.YMD),
        };
      });

      return months;
    },
    [onlyCurrentMonth]
  );

  if (isLoading) return <CasLoader />;

  return (
    <CasCRUDTable
      entityTitle=""
      entity={entity}
      defaultOrder="desc"
      columns={[
        {
          field: 'changedDate',
          label: 'Changed',
          render: CasRenderRelativeDateWithName,
        },
        {
          field: 'typeDesc',
          label: 'Type',
        },
        {
          field: 'whenDate',
          label: 'When',
          sx: getColorByDateStatus,
          render: ({ row }) => formatDate(row.whenDate, DateFormat.MDY),
        },
        {
          field: 'what',
          showEditOnclick: true,
          clickedCellFormInfo: (row) => getEditRowInfo(row.whatID, row.typeDesc),
        },
        {
          field: 'from',
          sortable: false,
          render: CasRenderChanges,
        },
        {
          field: 'to',
          sortable: false,
          render: CasRenderChanges,
        },
      ]}
      filters={[
        {
          type: 'select',
          name: 'TypeDesc',
          label: 'Type',
          options: allTypeOptions,
        },
        {
          type: 'select',
          name: 'WhenDateMonth',
          label: 'When (month)',
          initialValue: currentPeriodStart,
          options: getPrevAndNextMonths(currentPeriodStart),
        },
      ]}
      editForm={null}
      showAddButton={false}
    />
  );
}
